import { AfterViewInit, ChangeDetectorRef, Directive, ElementRef, HostListener, Input, OnDestroy, OnInit, Renderer2 } from "@angular/core";
import { DigitalAssetScreenSize } from "clearline-api";
import { getDigitalAssetScreenSize } from "clearline-common";
import { Subject } from "rxjs";
import { debounceTime, filter, takeUntil } from "rxjs/operators";

const referenceHeightScreensAsset = {
  Full: 1080,
  Vertical: 1920,
  Tablet: 1024,
  Compact: 812
};
const aspectRatioScreensAsset = {
  Full: 16 / 9, // 1920 / 1080 px
  Vertical: 9 / 16, // 1080 / 1920 px
  Tablet: 3 / 4, // 768 / 1024 px
  Compact: 375 / 812
};
const contentConfig = {
  a4PortraitWidth: 297,
  a4PortraitHeight: 210,
  titleLengthLimit: 30,
  titlePadding: 30,
  titlePaddingMobile: 10,
  descRowNum: 2,
  descRowNumLarge: 3
};
const directiveConfig = {
  resizeDebounce: 1,
  textChangeDebounce: 50
};

interface TemplateDimensions {
  width: number;
  height: number;
  currentAspectRatio: number;
  isAspectRatioBigger: boolean;
  contentHeight: number;
  scale: number;
}

type TextChangeType = "title" | "description";

@Directive({
  selector: "[libHtmlTemplate]",
  standalone: true,
  exportAs: "libHtmlTemplate"
})
export class HtmlTemplateDirective implements OnInit, AfterViewInit, OnDestroy {
  @Input() responsive = false;
  @Input() isLowResolutionScreen = false;
  @Input() isPrintView = false;
  @Input() printBackgroundColor = "white";
  @Input() isTextFitEnabled = false;

  @Input() set titleText(value: string | null | undefined) {
    if (this._titleText !== value && !this.isTitleFirstChange) {
      this.textChange$.next("title");
    }

    this._titleText = value;
    this.isTitleFirstChange = false;
  }

  @Input() set descriptionText(value: string | null | undefined) {
    if (this._descriptionText !== value && !this.isDescriptionFirstChange) {
      this.textChange$.next("description");
    }

    this._descriptionText = value;
    this.isDescriptionFirstChange = false;
  }

  private _titleText: string | null | undefined;
  private isTitleFirstChange = true;
  private _descriptionText: string | null | undefined;
  private isDescriptionFirstChange = true;
  private titleElement: HTMLElement | null = null;
  private descElement: HTMLElement | null = null;

  private resize$ = new Subject<void>();
  private textChange$ = new Subject<TextChangeType>();
  private destroy$ = new Subject<void>();

  currentScreenSize: DigitalAssetScreenSize | null = null;

  @Input() set globalScreenSize(screenSize: DigitalAssetScreenSize | null) {
    this.applyScreenSize(screenSize);
    this.applyStyles();
  }

  @HostListener("window:resize") onResize() {
    this.resize$.next();
  }

  constructor(private element: ElementRef, private renderer: Renderer2, private cd: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.resize$.pipe(debounceTime(directiveConfig.resizeDebounce), takeUntil(this.destroy$)).subscribe(() => {
      if (this.responsive) {
        this.updateScreenSize();
      }
      this.applyStyles();
    });

    this.textChange$
      .pipe(
        debounceTime(directiveConfig.textChangeDebounce),
        filter(() => this.isTextFitEnabled),
        takeUntil(this.destroy$)
      )
      .subscribe((type: TextChangeType) => {
        if (type === "title") {
          this.applyTitleStyles();
        } else if (type === "description") {
          this.applyDescriptionStyles();
        }
      });
  }

  ngAfterViewInit(): void {
    this.titleElement = this.element.nativeElement.querySelector(".template-title");
    this.descElement = this.element.nativeElement.querySelector(".template-desc");

    if (this.responsive) {
      this.updateScreenSize();
    }

    this.setInitialCssVariables();
    this.applyStyles();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyScreenSize(screenSize: DigitalAssetScreenSize | null) {
    if (screenSize && this.currentScreenSize !== screenSize) {
      if (this.currentScreenSize) {
        this.renderer.removeClass(this.element.nativeElement, this.currentScreenSize);
      }

      this.currentScreenSize = screenSize;
      this.renderer.addClass(this.element.nativeElement, this.currentScreenSize);
      this.cd.detectChanges();
    }
  }

  private updateScreenSize() {
    const currentScreenSize: DigitalAssetScreenSize = getDigitalAssetScreenSize(this.isLowResolutionScreen, true);

    this.applyScreenSize(currentScreenSize);
  }

  private setInitialCssVariables(): void {
    this.applyCssVariable("title-padding", `${contentConfig.titlePadding}px`);
    this.applyCssVariable("title-padding-mobile", `${contentConfig.titlePaddingMobile}px`);
    this.applyCssVariable("desc-row-num", contentConfig.descRowNum);
  }

  private applyStyles() {
    if (this.element.nativeElement.clientWidth) {
      this.applyStylesForPrint();

      const { width, height, currentAspectRatio, isAspectRatioBigger, contentHeight, scale } =
        this.getTemplateDimensions() as TemplateDimensions;

      if (this.responsive) {
        const contentWidth = isAspectRatioBigger ? height * currentAspectRatio : width;

        this.applyCssVariable("width", `${contentWidth}px`);
      } else {
        this.applyCssVariable("width", `100%`);
      }

      this.applyCssVariable("height", `${contentHeight}px`);
      this.applyCssVariable("scale", scale);

      if (this.isTextFitEnabled) {
        this.applyTitleStyles(scale);
        this.applyDescriptionStyles();
      }
    }
  }

  private applyTitleStyles(scale?: number): void {
    if (!scale) {
      scale = this.getTemplateDimensions()?.scale;
    }

    if (this.titleElement && scale) {
      this.renderer.removeStyle(this.titleElement, "font-size");
      this.renderer.removeStyle(this.titleElement, "max-height");

      const titleLength = this.titleElement.textContent?.trim().length || 0;
      const isOverflowing = this.titleElement.scrollWidth > this.titleElement.clientWidth;

      if (isOverflowing) {
        const currentFontSize = parseFloat(getComputedStyle(this.titleElement).fontSize);
        const availableWidth = this.titleElement.clientWidth - 2 * contentConfig.titlePadding * scale;
        let scrollWidth = this.titleElement.scrollWidth;

        if (titleLength > contentConfig.titleLengthLimit) {
          scrollWidth = (scrollWidth / titleLength) * contentConfig.titleLengthLimit;
        }

        const newFontSize = (currentFontSize * availableWidth) / scrollWidth;
        const lineHeight = parseFloat(getComputedStyle(this.titleElement).lineHeight) / currentFontSize;

        this.renderer.setStyle(this.titleElement, "font-size", `${newFontSize}px`);
        this.renderer.setStyle(this.titleElement, "max-height", `${newFontSize * lineHeight}px`);
      } else {
        const originalFontSize = parseFloat(getComputedStyle(this.titleElement).fontSize);
        const originalLineHeight = parseFloat(getComputedStyle(this.titleElement).lineHeight) / originalFontSize;
        this.renderer.setStyle(this.titleElement, "max-height", `${originalFontSize * originalLineHeight}px`);
      }
    }
  }

  private applyDescriptionStyles(): void {
    if (this.descElement) {
      const lineHeight = parseFloat(getComputedStyle(this.descElement).lineHeight);
      const isOverflowing = this.descElement.scrollHeight > Math.round(lineHeight * contentConfig.descRowNum);

      if (isOverflowing) {
        this.renderer.addClass(this.element.nativeElement, "long-description");
        this.applyCssVariable("desc-row-num", contentConfig.descRowNumLarge);
      } else {
        this.renderer.removeClass(this.element.nativeElement, "long-description");
        this.applyCssVariable("desc-row-num", `${contentConfig.descRowNum}`);
      }
    }
  }

  private applyCssVariable(variableName: string, variableValue: number | string): void {
    this.renderer.setStyle(this.element.nativeElement, `--${variableName}`, `${variableValue}`, 2);
  }

  private applyStylesForPrint(): void {
    if (this.isPrintView) {
      const parentElement = this.element.nativeElement.parentElement;

      if (parentElement) {
        this.renderer.setStyle(parentElement, "background", this.printBackgroundColor);
      }

      this.renderer.setStyle(this.element.nativeElement, "width", `${contentConfig.a4PortraitWidth}mm`);
      this.renderer.setStyle(this.element.nativeElement, "height", `${contentConfig.a4PortraitHeight}mm`);
    }
  }

  private getTemplateDimensions(): TemplateDimensions | null {
    if (this.element.nativeElement.clientWidth) {
      const width = this.element.nativeElement.clientWidth;
      const height = this.element.nativeElement.clientHeight;
      const currentAspectRatio: number = this.getCurrentScreenSizeAspectRatio();
      const currentReferenceHeight: number = this.getCurrentScreenSizeReferenceHeight();
      const isAspectRatioBigger: boolean = width / height > currentAspectRatio;
      const contentHeight = isAspectRatioBigger && this.responsive ? height : width / currentAspectRatio;
      const scale: number = contentHeight / currentReferenceHeight;

      return { width, height, currentAspectRatio, isAspectRatioBigger, contentHeight, scale };
    }

    return null;
  }

  private getCurrentScreenSizeReferenceHeight(): number {
    return referenceHeightScreensAsset[this.currentScreenSize || DigitalAssetScreenSize.Full];
  }

  private getCurrentScreenSizeAspectRatio(): number {
    return aspectRatioScreensAsset[this.currentScreenSize || DigitalAssetScreenSize.Full];
  }
}
